import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useCartStore } from '../../store/cartStore';
import type { Product } from '../../types/product';

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'detail' | 'review' | 'inquiry'>('detail');
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      setProduct(data);
    } catch (error) {
      console.error('상품 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      alert(`${product.name} ${quantity}개가 장바구니에 추가되었습니다!`);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      addItem(product, quantity);
      window.REACT_APP_NAVIGATE('/cart');
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && product && newQuantity <= product.stock_quantity) {
      setQuantity(newQuantity);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">상품을 찾을 수 없습니다</h2>
          <Link to="/products" className="text-teal-600 hover:text-teal-700 font-medium">
            상품 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* 상단 헤더 */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* 로고 */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
              <span className="text-2xl font-bold" style={{ fontFamily: "'Caveat', cursive", color: '#FF6B35' }}>
                쿨팡
              </span>
              <span className="px-2 py-0.5 rounded" style={{ 
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }}>
                정
              </span>
              <span className="text-xl font-bold text-gray-900">
                정희네농원
              </span>
            </Link>

            {/* 네비게이션 */}
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">
                홈
              </Link>
              <Link to="/products" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">
                상품
              </Link>
              <Link to="/cart" className="text-gray-700 hover:text-teal-600 font-medium transition-colors cursor-pointer">
                장바구니
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* 브레드크럼 */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-teal-600 cursor-pointer">
              <i className="ri-home-5-line"></i>
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/products" className="text-gray-600 hover:text-teal-600 cursor-pointer">상품</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-400">{product.category}</span>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-800 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* 왼쪽: 상품 이미지 */}
          <div className="space-y-4">
            {/* 메인 이미지 */}
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            
            {/* 썸네일 이미지 (추후 확장 가능) */}
            <div className="grid grid-cols-5 gap-2">
              <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden border-2 border-teal-600 cursor-pointer">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* 오른쪽: 상품 정보 */}
          <div className="space-y-6">
            {/* 카테고리 */}
            <div>
              <span className="inline-block text-sm text-gray-600 mb-2">{product.category}</span>
            </div>

            {/* 상품명 */}
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              {product.name}
            </h1>

            {/* 평점 (추후 실제 데이터 연동) */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i key={star} className="ri-star-fill text-yellow-400 text-lg"></i>
                ))}
              </div>
              <span className="text-sm text-gray-600">(128)</span>
            </div>

            {/* 가격 */}
            <div className="border-t border-b border-gray-200 py-6">
              <div className="flex items-baseline space-x-3">
                <span className="text-3xl font-bold text-gray-900">
                  {product.price.toLocaleString()}원
                </span>
              </div>
            </div>

            {/* 배송 정보 */}
            <div className="space-y-3 text-sm">
              <div className="flex items-start">
                <span className="w-24 text-gray-600">배송</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">무료배송</p>
                  <p className="text-gray-500 mt-1">정희네농원 직송 · 신선도 보장</p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-24 text-gray-600">판매자</span>
                <span className="text-gray-900">정희네농원</span>
              </div>
            </div>

            {/* 재고 상태 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">재고 수량</span>
                <span className={`text-sm font-semibold ${
                  product.stock_quantity > 10 ? 'text-green-600' : 
                  product.stock_quantity > 0 ? 'text-orange-600' : 'text-red-600'
                }`}>
                  {product.stock_quantity > 0 ? `${product.stock_quantity}개 남음` : '품절'}
                </span>
              </div>
            </div>

            {product.stock_quantity > 0 && (
              <>
                {/* 수량 선택 */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-900">
                    수량
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line text-lg"></i>
                    </button>
                    <input
                      type="text"
                      value={quantity}
                      readOnly
                      className="w-16 h-10 text-center border border-gray-300 rounded-md font-medium"
                    />
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= product.stock_quantity}
                      className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-lg"></i>
                    </button>
                  </div>
                </div>

                {/* 총 금액 */}
                <div className="bg-gray-50 rounded-lg p-5">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-medium text-gray-700">총 상품금액</span>
                    <div className="text-right">
                      <span className="text-sm text-gray-500 mr-2">총 수량 {quantity}개</span>
                      <span className="text-2xl font-bold text-teal-600">
                        {(product.price * quantity).toLocaleString()}원
                      </span>
                    </div>
                  </div>
                </div>

                {/* 구매 버튼 */}
                <div className="grid grid-cols-2 gap-3 pt-4">
                  <button
                    onClick={handleAddToCart}
                    className="h-14 bg-white border-2 border-teal-600 text-teal-600 rounded-lg font-bold text-base hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    장바구니
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="h-14 bg-teal-600 text-white rounded-lg font-bold text-base hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    바로구매
                  </button>
                </div>
              </>
            )}

            {product.stock_quantity === 0 && (
              <div className="bg-gray-100 rounded-lg p-6 text-center">
                <i className="ri-error-warning-line text-3xl text-gray-400 mb-2"></i>
                <p className="text-gray-600 font-medium">현재 품절된 상품입니다</p>
              </div>
            )}
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="border-t border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('detail')}
              className={`py-4 px-2 font-medium text-base border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'detail'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              상품상세
            </button>
            <button
              onClick={() => setActiveTab('review')}
              className={`py-4 px-2 font-medium text-base border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'review'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              상품평 <span className="text-teal-600">(128)</span>
            </button>
            <button
              onClick={() => setActiveTab('inquiry')}
              className={`py-4 px-2 font-medium text-base border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'inquiry'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              상품문의
            </button>
          </div>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="py-12">
          {activeTab === 'detail' && (
            <div className="max-w-4xl mx-auto">
              {/* 상품 상세 내용 */}
              {product.detail_content && (
                <div className="bg-gray-50 rounded-lg p-8 mb-8">
                  <div className="prose prose-lg max-w-none">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {product.detail_content}
                    </div>
                  </div>
                </div>
              )}

              {/* 상세 이미지들 */}
              {product.detail_images && product.detail_images.length > 0 && (
                <div className="space-y-4 mb-8">
                  {product.detail_images.map((imageUrl: string, index: number) => (
                    <div key={index} className="w-full">
                      <img
                        src={imageUrl}
                        alt={`${product.name} 상세 이미지 ${index + 1}`}
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* 기본 상품 설명 (detail_content가 없을 때만) */}
              {!product.detail_content && (
                <div className="bg-gray-50 rounded-lg p-8 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">상품 상세정보</h2>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {product.description}
                    </p>
                  </div>
                </div>
              )}

              {/* 상품 정보 고시 */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">상품정보 제공고시</h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex">
                    <dt className="w-40 text-gray-600">식품의 유형</dt>
                    <dd className="flex-1 text-gray-900">{product.category}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-40 text-gray-600">생산자</dt>
                    <dd className="flex-1 text-gray-900">{product.farmer_name || '정희네농원'}</dd>
                  </div>
                  {product.weight_info && (
                    <div className="flex">
                      <dt className="w-40 text-gray-600">중량/구성</dt>
                      <dd className="flex-1 text-gray-900">{product.weight_info}</dd>
                    </div>
                  )}
                  <div className="flex">
                    <dt className="w-40 text-gray-600">원산지</dt>
                    <dd className="flex-1 text-gray-900">국내산</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-40 text-gray-600">보관방법</dt>
                    <dd className="flex-1 text-gray-900">냉장보관</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-40 text-gray-600">소비자상담</dt>
                    <dd className="flex-1 text-gray-900">1588-0000</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {activeTab === 'review' && (
            <div className="max-w-4xl mx-auto">
              {/* 평점 요약 */}
              <div className="bg-gray-50 rounded-lg p-8 mb-8">
                <div className="flex items-center space-x-8">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-900 mb-2">4.8</div>
                    <div className="flex items-center justify-center mb-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-yellow-400 text-xl"></i>
                      ))}
                    </div>
                    <div className="text-sm text-gray-600">128개 상품평</div>
                  </div>
                  <div className="flex-1 space-y-2">
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <div key={rating} className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600 w-8">{rating}점</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400"
                            style={{ width: `${rating === 5 ? 85 : rating === 4 ? 10 : 3}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {rating === 5 ? 109 : rating === 4 ? 13 : rating === 3 ? 4 : rating === 2 ? 1 : 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 상품평 목록 */}
              <div className="space-y-6">
                {[1, 2, 3].map((review) => (
                  <div key={review} className="border-b border-gray-200 pb-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-gray-500 text-xl"></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="font-medium text-gray-900">구매자{review}</span>
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i key={star} className="ri-star-fill text-yellow-400 text-sm"></i>
                            ))}
                          </div>
                          <span className="text-sm text-gray-500">2024.12.{20 - review}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-3">
                          신선하고 맛있어요! 배송도 빠르고 포장도 꼼꼼하게 잘 되어있습니다. 
                          다음에도 또 구매할게요. 강력 추천합니다!
                        </p>
                        <div className="flex items-center space-x-2">
                          <button className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer whitespace-nowrap">
                            <i className="ri-thumb-up-line mr-1"></i>
                            도움돼요 <span className="text-teal-600">24</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-8">
                <button className="px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium cursor-pointer whitespace-nowrap">
                  상품평 더보기
                </button>
              </div>
            </div>
          )}

          {activeTab === 'inquiry' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <h3 className="font-bold text-gray-900 mb-2">
                  <i className="ri-information-line text-blue-600 mr-2"></i>
                  상품문의 안내
                </h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 상품에 대한 문의를 남기는 공간입니다.</li>
                  <li>• 답변은 영업일 기준 2~3일 소요됩니다.</li>
                  <li>• 배송/교환/환불 문의는 고객센터를 이용해주세요.</li>
                </ul>
              </div>

              <div className="text-center py-12">
                <i className="ri-question-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-600 mb-6">아직 등록된 문의가 없습니다.</p>
                <button className="px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap">
                  문의하기
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
